import React, { useState, useRef, useLayoutEffect } from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import ButtonClick from "../buttonClick";
import { useOnClickOutside } from "../../hooks/useOnClickOutside";
import styles from "./dropdownMenu.module.css";
import { createPortal } from "react-dom";

const DropdownMenu = ({
  buttonChildren, // Children passthough to button.
  children, // Children passthough for the dropdown menu (usually list elements).
  className, // CSS class for the combined button and dropdown.
  closeOnClick, // Bool controlling whether menu should close on selection click or not.
  dropdownClassName, // CSS class just for the dropdown menu
  buttonClassName="",
  svgClassName,
  portal = false,
  ...rest
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [portalStyle, setPortalStyle] = useState({});
  const menuRef = useRef();
  const buttonWrapRef = useRef(null);
  const portalMenuRef = useRef(null);

  useOnClickOutside(portal ? portalMenuRef : menuRef, () => setShowMenu(false));

  const containerClassNames = classnames(className, styles.container);

  const dropdownClassNames = classnames(dropdownClassName, styles.menu, {
    [styles.show]: showMenu
  });

  const handleOnClick = e => {
    e.preventDefault();
    setShowMenu(!showMenu);
  };

  useLayoutEffect(() => {
    if (!portal || !showMenu || !buttonWrapRef.current) return;

    const rect = buttonWrapRef.current.getBoundingClientRect();

    setPortalStyle({
      position: "fixed",
      top: rect.bottom,
      left: rect.left,
      width: rect.width,
      zIndex: 9999
    });
  }, [portal, showMenu]);

  const menu = (
    <ul
      className={dropdownClassNames}
      style={portal ? portalStyle : undefined}
      onClick={closeOnClick ? handleOnClick : undefined}
      ref={portal ? portalMenuRef : null}
    >
      {children}
    </ul>
  );


  return (
    <div className={containerClassNames} ref={menuRef}>
      <div ref={buttonWrapRef}>
        <ButtonClick
          isActive={showMenu}
          menuOpen={showMenu}
          svgClassName={svgClassName}
          onClick={handleOnClick}
          onMouseEnter={() => setIsMouseOver(true)}
          onMouseLeave={() => setIsMouseOver(false)}
          className={isMouseOver ? buttonClassName+" button rounded" : buttonClassName}
          {...rest}
        >
          {buttonChildren}
        </ButtonClick>
      </div>
      {portal && showMenu && portalStyle.width ? createPortal(menu, document.body) : menu}
    </div>
  );
};

DropdownMenu.propTypes = {
  buttonChildren: PropTypes.object,
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  className: PropTypes.string,
  closeOnClick: PropTypes.bool,
  dropdownClassName: PropTypes.string,
  override: PropTypes.func,
  overrideReset: PropTypes.bool
};

export default DropdownMenu;
